// src/pages/ServicesPage.jsx
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Clock, CheckCircle } from "lucide-react";
import { useTenant } from "../context/TenantContext";
import { useAuth } from "../context/AuthContext";
import { ServiceVisual } from "../components/ui";

function groupByCategory(services) {
  const groups = {};
  services.forEach(s => {
    const cat = s.category || "Other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(s);
  });
  return groups;
}

export default function ServicesPage() {
  const navigate   = useNavigate();
  const { slug }   = useParams();
  const { user }   = useAuth();
  const { tenant } = useTenant();

  if (!tenant) return null;
  const grouped = groupByCategory(tenant.services || []);
  const categories = Object.keys(grouped);

  return (
    <div className="py-16 bg-surface-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="badge-brand mx-auto mb-3 inline-flex">Our Services</div>
          <h1 className="font-semibold text-4xl md:text-5xl text-ink-900 mb-4">
            Our Services,{" "}
            <span className="text-gradient italic">Crafted for You</span>
          </h1>
          <p className="text-ink-400 font-sans text-base max-w-xl mx-auto">
            Every service is performed by skilled professionals.
            Book online in seconds — no calls, no waiting.
          </p>
        </div>

        {/* Service categories */}
        {categories.map(category => (
          <div key={category} className="mb-12">
            <h2 className="font-semibold text-xl text-ink-700 mb-5 pb-2 border-b border-surface-200">
              {category}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {grouped[category].map(service => (
                <div key={service.id}
                  className="card p-6 flex items-start gap-4 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200">
                  <ServiceVisual service={service} category={tenant.category} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-ink-900 text-base">{service.name}</h3>
                      {service.popular && (
                        <span className="badge-brand text-[10px] px-2 py-0.5">Popular</span>
                      )}
                    </div>
                    <p className="text-ink-400 text-xs font-sans leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-ink-400 text-xs font-mono">
                        <Clock size={12} />
                        <span>{service.duration} min</span>
                      </div>
                      <span className="font-semibold text-brand-600 text-xl font-semibold">
                        ${service.price}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* What's included */}
        <div className="card p-8 mb-10">
          <h2 className="font-semibold text-xl text-ink-900 mb-6">Every Appointment Includes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Professional consultation",
              "Experienced, vetted staff",
              "Clean & sanitized environment",
              "Relaxed, no-rush experience",
              "Easy online scheduling",
              "Satisfaction guarantee",
            ].map(item => (
              <div key={item} className="flex items-center gap-2.5 text-ink-600 text-sm font-sans">
                <CheckCircle size={16} className="text-brand-500 flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate(user ? `/${slug}/book` : "/signup")}
            className="btn-primary px-10 py-4 text-base inline-flex items-center gap-2">
            {user ? "Book an Appointment" : "Get Started Free"} <ArrowRight size={18} />
          </button>
          <p className="text-ink-400 text-xs font-sans mt-3">
            No calls, no waiting — book in under 60 seconds.
          </p>
        </div>
      </div>
    </div>
  );
}
